import { supabase } from '../lib/supabase';
import { User } from '../types/models';

export interface BulkOperationResult {
  processedCount: number;
  failedCount: number;
  errors: Array<{ userId: string; error: string }>;
}

export class UserManagementService {
  private readonly userSelectVariants: string[] = [
    `
      *,
      posts:posts(count),
      stories:stories(count)
    `,
    `
      *,
      posts:posts(count),
      stories:stories(count)
    `,
    `
      *
    `
  ];

  private async fetchUserByIdWithFallback(userId: string): Promise<any | null> {
    for (const selectClause of this.userSelectVariants) {
      const { data, error } = await supabase
        .from('users')
        .select(selectClause)
        .eq('id', userId)
        .single();

      if (!error) return data;
    }
    return null;
  }

  private async fetchAllUsersWithFallback(): Promise<any[] | null> {
    for (const selectClause of this.userSelectVariants) {
      const { data, error } = await supabase
        .from('users')
        .select(selectClause)
        .order('created_at', { ascending: false });

      if (!error) return data || [];
    }
    return null;
  }

  private async fetchFollowCountsForUsers(userIds: string[]): Promise<{
    followersByUser: Map<string, number>;
    followingByUser: Map<string, number>;
  }> {
    const followersByUser = new Map<string, number>();
    const followingByUser = new Map<string, number>();

    if (userIds.length === 0) {
      return { followersByUser, followingByUser };
    }

    const batchSize = 500;
    for (let index = 0; index < userIds.length; index += batchSize) {
      const batch = userIds.slice(index, index + batchSize);

      const [{ data: followerRows }, { data: followingRows }] = await Promise.all([
        supabase.from('follows').select('following_id').in('following_id', batch),
        supabase.from('follows').select('follower_id').in('follower_id', batch)
      ]);

      (followerRows || []).forEach((row: any) => {
        const key = row?.following_id;
        if (!key) return;
        followersByUser.set(key, (followersByUser.get(key) || 0) + 1);
      });

      (followingRows || []).forEach((row: any) => {
        const key = row?.follower_id;
        if (!key) return;
        followingByUser.set(key, (followingByUser.get(key) || 0) + 1);
      });
    }

    return { followersByUser, followingByUser };
  }

  /**
   * Fetch coach details from the separate coaches table.
   * Returns a map of user_id → details JSON object.
   */
  private async fetchCoachDetailsForUsers(userIds: string[]): Promise<Map<string, any>> {
    const coachDetailsByUser = new Map<string, any>();

    if (userIds.length === 0) return coachDetailsByUser;

    try {
      const batchSize = 500;
      for (let index = 0; index < userIds.length; index += batchSize) {
        const batch = userIds.slice(index, index + batchSize);
        const { data, error } = await supabase
          .from('coaches')
          .select('user_id, details')
          .in('user_id', batch);

        if (error) {
          console.warn('[UserManagement] Failed to fetch coach details:', error.message);
          continue;
        }

        (data || []).forEach((row: any) => {
          if (row?.user_id && row?.details) {
            coachDetailsByUser.set(row.user_id, row.details);
          }
        });
      }
    } catch (error: any) {
      console.warn('[UserManagement] Error fetching coach details:', error?.message || error);
    }

    return coachDetailsByUser;
  }

  /**
   * Fetch organization contact_info from the separate organizations table.
   * Returns a map of user_id → contact_info JSON object.
   */
  private async fetchOrgContactInfoForUsers(userIds: string[]): Promise<Map<string, any>> {
    const orgInfoByUser = new Map<string, any>();

    if (userIds.length === 0) return orgInfoByUser;

    try {
      const batchSize = 500;
      for (let index = 0; index < userIds.length; index += batchSize) {
        const batch = userIds.slice(index, index + batchSize);
        const { data, error } = await supabase
          .from('organizations')
          .select('user_id, contact_info')
          .in('user_id', batch);

        if (error) {
          console.warn('[UserManagement] Failed to fetch org contact_info:', error.message);
          continue;
        }

        (data || []).forEach((row: any) => {
          if (row?.user_id && row?.contact_info) {
            orgInfoByUser.set(row.user_id, row.contact_info);
          }
        });
      }
    } catch (error: any) {
      console.warn('[UserManagement] Error fetching org contact_info:', error?.message || error);
    }

    return orgInfoByUser;
  }

  private async safeMutation(label: string, action: () => PromiseLike<any> | any): Promise<void> {
    try {
      const result = await action();
      if (result?.error) {
        console.warn(`[delete-user] ${label}:`, result.error.message || result.error);
      }
    } catch (error: any) {
      console.warn(`[delete-user] ${label}:`, error?.message || error);
    }
  }

  private async safeDeleteByColumn(table: string, column: string, value: string | null | undefined): Promise<void> {
    if (!value) return;
    await this.safeMutation(`${table}.${column}`, () =>
      supabase.from(table).delete().eq(column, value)
    );
  }

  private async safeNullByColumn(table: string, column: string, value: string | null | undefined): Promise<void> {
    if (!value) return;
    await this.safeMutation(`${table}.${column} -> null`, () =>
      supabase.from(table).update({ [column]: null }).eq(column, value)
    );
  }

  private async resolveUserIdentifiers(userId: string): Promise<{ id: string; uid?: string }> {
    const { data: byId } = await supabase
      .from('users')
      .select('id, uid')
      .eq('id', userId)
      .maybeSingle();

    if (byId?.id) {
      return { id: byId.id, uid: byId.uid || undefined };
    }

    const { data: byUid } = await supabase
      .from('users')
      .select('id, uid')
      .eq('uid', userId)
      .maybeSingle();

    if (byUid?.id) {
      return { id: byUid.id, uid: byUid.uid || undefined };
    }

    return { id: userId };
  }

  private async cleanupUserDependencies(internalId: string, externalUid?: string): Promise<void> {
    // Notifications
    await this.safeDeleteByColumn('notifications', 'sender_id', internalId);
    await this.safeDeleteByColumn('notifications', 'receiver_id', internalId);
    await this.safeDeleteByColumn('notifications', 'recipient_id', internalId);

    // Role/profile tables
    await this.safeDeleteByColumn('athletes', 'user_id', internalId);
    await this.safeDeleteByColumn('coaches', 'user_id', internalId);
    await this.safeDeleteByColumn('parents', 'user_id', internalId);
    await this.safeDeleteByColumn('organizations', 'user_id', internalId);

    // Content and interactions
    await this.safeDeleteByColumn('post_likes', 'user_id', internalId);
    await this.safeDeleteByColumn('post_comments', 'user_id', internalId);
    await this.safeDeleteByColumn('posts', 'user_id', internalId);
    await this.safeDeleteByColumn('stories', 'user_id', internalId);
    await this.safeDeleteByColumn('moment_likes', 'user_id', internalId);
    await this.safeDeleteByColumn('moment_comments', 'user_id', internalId);
    await this.safeDeleteByColumn('moments', 'user_id', internalId);
    await this.safeDeleteByColumn('talent_video_likes', 'user_id', internalId);
    await this.safeDeleteByColumn('talent_video_comments', 'user_id', internalId);
    await this.safeDeleteByColumn('talent_videos', 'user_id', internalId);
    await this.safeDeleteByColumn('post_saves', 'user_id', internalId);
    await this.safeDeleteByColumn('forum_saves', 'user_id', internalId);
    await this.safeMutation('repost (author/sharer)', () =>
      supabase.from('repost').delete().or(`sharer_id.eq.${internalId},original_author_id.eq.${internalId}`)
    );

    // Social graph
    await this.safeDeleteByColumn('followers', 'follower_id', internalId);
    await this.safeDeleteByColumn('followers', 'following_id', internalId);
    await this.safeDeleteByColumn('follows', 'follower_id', internalId);
    await this.safeDeleteByColumn('follows', 'following_id', internalId);
    await this.safeDeleteByColumn('connection_activity', 'actor_id', internalId);
    await this.safeDeleteByColumn('connection_activity', 'target_id', internalId);
    await this.safeMutation('friendships (user1/user2)', () =>
      supabase.from('friendships').delete().or(`user1_id.eq.${internalId},user2_id.eq.${internalId}`)
    );
    await this.safeMutation('friend_requests (sender/receiver/recipient)', () =>
      supabase.from('friend_requests').delete().or(
        `sender_id.eq.${internalId},receiver_id.eq.${internalId},recipient_id.eq.${internalId}`
      )
    );
    await this.safeMutation('blocked_users (blocker/blocked)', () =>
      supabase.from('blocked_users').delete().or(`blocker_id.eq.${internalId},blocked_id.eq.${internalId}`)
    );
    await this.safeMutation('organization_connections (sender/recipient)', () =>
      supabase.from('organization_connections').delete().or(`sender_id.eq.${internalId},recipient_id.eq.${internalId}`)
    );

    // Group ownership/membership
    await this.safeDeleteByColumn('group_members', 'user_id', internalId);
    const { data: ownedGroups } = await supabase
      .from('groups')
      .select('id')
      .eq('creator_id', internalId);

    if (Array.isArray(ownedGroups) && ownedGroups.length > 0) {
      const groupIds = ownedGroups.map((group: any) => group.id).filter(Boolean);
      if (groupIds.length > 0) {
        await this.safeMutation('group_members (owned groups)', () =>
          supabase.from('group_members').delete().in('group_id', groupIds)
        );
      }
    }
    await this.safeDeleteByColumn('groups', 'creator_id', internalId);

    // Attendance and events
    await this.safeDeleteByColumn('event_submissions', 'user_id', internalId);
    await this.safeDeleteByColumn('event_participations', 'user_id', internalId);
    await this.safeDeleteByColumn('attendance_records', 'user_id', internalId);
    await this.safeDeleteByColumn('office_user_assignments', 'user_id', internalId);
    await this.safeDeleteByColumn('track_bests', 'user_id', internalId);
    await this.safeDeleteByColumn('peer_verification_requests', 'user_id', internalId);

    // Legacy/user_uid references used by attendance flow
    await this.safeDeleteByColumn('attendance_records', 'user_uid', externalUid || internalId);
    await this.safeDeleteByColumn('office_user_assignments', 'user_uid', externalUid || internalId);
    if (externalUid && externalUid !== internalId) {
      await this.safeDeleteByColumn('attendance_records', 'user_uid', internalId);
      await this.safeDeleteByColumn('office_user_assignments', 'user_uid', internalId);
    }

    // References often configured as ON DELETE SET NULL
    await this.safeNullByColumn('messages', 'sender_id', internalId);
    await this.safeNullByColumn('announcements', 'created_by', internalId);
    await this.safeNullByColumn('bulk_operation_logs', 'executed_by', internalId);
    await this.safeNullByColumn('events', 'organizer_id', internalId);
    await this.safeNullByColumn('reports', 'reporter_id', internalId);

    // References commonly configured with NO ACTION
    await this.safeDeleteByColumn('reports', 'reported_user_id', internalId);
    await this.safeDeleteByColumn('news', 'author_id', internalId);
  }

  private isDependencyBlockedDeleteError(error: any): boolean {
    if (!error) return false;

    const code = typeof error.code === 'string' ? error.code : '';
    const message = String(error.message || '').toLowerCase();
    const details = String(error.details || '').toLowerCase();

    if (code === '23503' || message.includes('foreign key constraint')) {
      return true;
    }

    if (code === '23502') {
      const isNotNullViolation =
        message.includes('null value in column') &&
        message.includes('violates not-null constraint');
      const isKnownDeleteCase =
        details.includes('relation "notifications"') ||
        details.includes('relation "messages"') ||
        details.includes('"sender_id"');

      if (isNotNullViolation || isKnownDeleteCase) {
        return true;
      }
    }

    return false;
  }

  private async secureSoftDeleteUser(userId: string, reason?: string): Promise<void> {
    const { error: updateError } = await supabase
      .from('users')
      .update({
        is_active: false,
        display_name: 'Deleted User',
        email: `deleted_${userId.substring(0, 8)}@deleted.amaplayer.com`,
        photo_url: null,
        bio: null,
        location: null,
        is_verified: false,
        username: `deleted_${userId.substring(0, 8)}`,
        status: 'deleted',
        suspension_reason: reason || 'Account deleted by admin',
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (updateError) throw updateError;
  }

  private getStringValue(value: any): string | undefined {
    if (value === null || value === undefined) return undefined;
    const normalized = String(value).trim();
    return normalized || undefined;
  }

  private getStringArrayValue(value: any): string[] | undefined {
    if (!value) return undefined;

    if (Array.isArray(value)) {
      const normalized = value
        .map((entry) => this.getStringValue(entry))
        .filter((entry): entry is string => Boolean(entry));
      return normalized.length ? normalized : undefined;
    }

    if (typeof value === 'string') {
      const normalized = value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
      return normalized.length ? normalized : undefined;
    }

    return undefined;
  }

  /**
   * Suspend a single user
   */
  async suspendUser(userId: string, reason?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          is_active: false,
          // status: 'suspended', // Map to column if exists
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
    } catch (error) {
      throw new Error(`Failed to suspend user: ${error}`);
    }
  }

  /**
   * Bulk suspend users
   */
  async bulkSuspendUsers(userIds: string[], reason?: string): Promise<BulkOperationResult> {
    const result: BulkOperationResult = { processedCount: 0, failedCount: 0, errors: [] };
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_active: false })
        .in('id', userIds);

      if (error) throw error;
      result.processedCount = userIds.length;
    } catch (error: any) {
      result.failedCount = userIds.length;
      result.errors.push({ userId: 'batch', error: error.message });
    }
    return result;
  }

  /**
   * Verify a single user
   */
  async verifyUser(userId: string, reason?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          is_verified: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
    } catch (error) {
      throw new Error(`Failed to verify user: ${error}`);
    }
  }

  /**
   * Bulk verify users
   */
  async bulkVerifyUsers(userIds: string[], reason?: string): Promise<BulkOperationResult> {
    const result: BulkOperationResult = { processedCount: 0, failedCount: 0, errors: [] };
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_verified: true })
        .in('id', userIds);

      if (error) throw error;
      result.processedCount = userIds.length;
    } catch (error: any) {
      result.failedCount = userIds.length;
      result.errors.push({ userId: 'batch', error: error.message });
    }
    return result;
  }

  /**
   * Bulk activate users
   */
  async bulkActivateUsers(userIds: string[], reason?: string): Promise<BulkOperationResult> {
    const result: BulkOperationResult = { processedCount: 0, failedCount: 0, errors: [] };
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_active: true, status: 'active', suspended_at: null, suspension_reason: null })
        .in('id', userIds);

      if (error) throw error;
      result.processedCount = userIds.length;
    } catch (error: any) {
      result.failedCount = userIds.length;
      result.errors.push({ userId: 'batch', error: error.message });
    }
    return result;
  }

  /**
   * Activate a single user
   */
  async activateUser(userId: string): Promise<void> {
    const { error } = await supabase.from('users').update({ is_active: true }).eq('id', userId);
    if (error) throw error;
  }

  async deleteUser(userId: string, reason?: string): Promise<void> {
    try {
      const { id: internalId, uid: externalUid } = await this.resolveUserIdentifiers(userId);

      // 1. Best-effort cleanup of dependent records (production-style flow).
      await this.cleanupUserDependencies(internalId, externalUid);

      // 2. Hard delete user row.
      const { error } = await supabase.from('users').delete().eq('id', internalId);

      if (error) {
        if (this.isDependencyBlockedDeleteError(error)) {
          console.warn('Hard delete blocked after dependency cleanup; applying secure soft delete.', error);
          await this.secureSoftDeleteUser(internalId, reason);
          return;
        }

        throw error;
      }
    } catch (error) {
      console.error('Delete user failed:', error);
      throw error;
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const data = await this.fetchUserByIdWithFallback(userId);
      if (!data) return null;

      const [{ count: followersCount }, { count: followingCount }, coachDetailsByUser, orgContactInfoByUser] = await Promise.all([
        supabase.from('follows').select('*', { count: 'exact', head: true }).eq('following_id', data.id),
        supabase.from('follows').select('*', { count: 'exact', head: true }).eq('follower_id', data.id),
        this.fetchCoachDetailsForUsers([data.id]),
        this.fetchOrgContactInfoForUsers([data.id])
      ]);

      const coachDetails = coachDetailsByUser.get(data.id);
      const orgContactInfo = orgContactInfoByUser.get(data.id);
      const enrichedData = {
        ...data,
        ...(coachDetails ? { _coachDetails: coachDetails } : {}),
        ...(orgContactInfo ? { _orgContactInfo: orgContactInfo } : {})
      };

      return this.mapToModel(enrichedData, {
        followersCount: typeof followersCount === 'number' ? followersCount : undefined,
        followingCount: typeof followingCount === 'number' ? followingCount : undefined
      });
    } catch (error) {
      throw new Error(`Failed to fetch user: ${error}`);
    }
  }

  /**
   * Update user
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return this.mapToModel(data);
    } catch (error) {
      throw new Error(`Failed to update user: ${error}`);
    }
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<any> {
    try {
      const { count: total } = await supabase.from('users').select('*', { count: 'exact', head: true });
      const { count: active } = await supabase.from('users').select('*', { count: 'exact', head: true }).eq('is_active', true);
      const { count: verified } = await supabase.from('users').select('*', { count: 'exact', head: true }).eq('is_verified', true);
      const { count: athletes } = await supabase.from('users').select('*', { count: 'exact', head: true }).eq('role', 'athlete');
      const { count: coaches } = await supabase.from('users').select('*', { count: 'exact', head: true }).eq('role', 'coach');
      const { count: organizations } = await supabase.from('users').select('*', { count: 'exact', head: true }).eq('role', 'organization');

      return {
        total: total || 0,
        active: active || 0,
        suspended: (total || 0) - (active || 0),
        verified: verified || 0,
        athletes: athletes || 0,
        coaches: coaches || 0,
        organizations: organizations || 0
      };
    } catch (error) {
      return { total: 0, active: 0, suspended: 0, verified: 0, athletes: 0, coaches: 0, organizations: 0 };
    }
  }

  /**
   * Get all users
   */
  /**
   * Get all users
   */
  async getAllUsers(): Promise<User[]> {
    try {
      const data = await this.fetchAllUsersWithFallback();
      if (!data) return [];

      const userIds = data
        .map((row: any) => row?.id)
        .filter((id: any): id is string => typeof id === 'string' && id.trim().length > 0);

      // Fetch coach details and org contact_info from their separate tables in parallel with follow counts
      const [{ followersByUser, followingByUser }, coachDetailsByUser, orgContactInfoByUser] = await Promise.all([
        this.fetchFollowCountsForUsers(userIds),
        this.fetchCoachDetailsForUsers(userIds),
        this.fetchOrgContactInfoForUsers(userIds)
      ]);

      return data.map((row) => {
        // Merge coach/org details into the row so mapToModel and normalizeSports can use them
        const coachDetails = coachDetailsByUser.get(row.id);
        const orgContactInfo = orgContactInfoByUser.get(row.id);
        const enrichedRow = {
          ...row,
          ...(coachDetails ? { _coachDetails: coachDetails } : {}),
          ...(orgContactInfo ? { _orgContactInfo: orgContactInfo } : {})
        };

        return this.mapToModel(enrichedRow, {
          followersCount: followersByUser.get(row.id),
          followingCount: followingByUser.get(row.id)
        });
      });
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      return [];
    }
  }

  /**
   * Get raw user row by ID (for debug/full-details display)
   */
  async getUserRawById(userId: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) return null;
      return data;
    } catch (error) {
      console.error('Failed to fetch raw user row:', error);
      return null;
    }
  }

  private normalizeSports(data: any): string[] {
    const extractSports = (value: any): string[] => {
      if (!value) return [];

      if (Array.isArray(value)) {
        return value
          .map((entry) => {
            if (typeof entry === 'string') return entry.trim();
            if (entry && typeof entry === 'object') {
              return String(entry.name || entry.title || entry.id || '').trim();
            }
            return '';
          })
          .filter(Boolean);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean);
      }

      if (typeof value === 'object') {
        const objectValue = value.name || value.title || value.id;
        if (objectValue) {
          return [String(objectValue).trim()].filter(Boolean);
        }
      }

      return [];
    };

    const coachProfile = data.coach_profile || data.coachProfile;
    const organizationProfile = data.organization_profile || data.organizationProfile || data.org_profile || data.orgProfile;
    // _coachDetails comes from the separate coaches table (details JSON column)
    const coachDetails = data._coachDetails;
    // _orgContactInfo comes from the separate organizations table (contact_info JSON column)
    const orgContactInfo = data._orgContactInfo;

    const candidates = [
      data.sports,
      data.sport,
      data.sport_details,
      data.favorite_sports,
      data.coaching_sports,
      data.selected_sports,
      data.athlete_profile?.sports,
      data.athlete_profile?.sport,
      coachDetails?.sport,
      coachDetails?.sports,
      orgContactInfo?.sports,
      orgContactInfo?.sport,
      coachProfile?.sports,
      coachProfile?.sport,
      coachProfile?.coaching_sports,
      coachProfile?.selected_sports,
      organizationProfile?.sports,
      organizationProfile?.sport,
      organizationProfile?.selected_sports,
    ];

    const normalized = candidates.flatMap(extractSports);
    return Array.from(new Set(normalized.map((sport) => sport.toLowerCase())))
      .map((lowerSport) => normalized.find((sport) => sport.toLowerCase() === lowerSport) as string);
  }

  private mapToModel(
    data: any,
    countOverrides?: { followersCount?: number; followingCount?: number }
  ): User {
    // Helper to safely extract count from Supabase relation array
    const getCount = (field: any) => {
      if (Array.isArray(field) && field.length > 0) {
        const parsed = Number(field[0].count);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    };

    // Prefer the live relation aggregate when available; fall back to the
    // denormalized *_count column only when the relation wasn't fetched.
    // Using Math.max was causing stale denormalized counts to override the
    // accurate live aggregate (e.g. posts_count=10 vs actual count=5).
    const pickBestCount = (relationField: any, staticField: any) => {
      if (Array.isArray(relationField)) {
        const parsed = Number(relationField[0]?.count);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      const staticCount = Number(staticField);
      return Number.isFinite(staticCount) ? staticCount : 0;
    };

    // Handles legacy fields that may store follower IDs directly as arrays/CSV.
    const getCollectionCount = (field: any) => {
      if (Array.isArray(field)) {
        if (field.length > 0 && typeof field[0] === 'object' && field[0] !== null && 'count' in field[0]) {
          return getCount(field);
        }
        return field.filter((entry) => entry !== null && entry !== undefined && String(entry).trim() !== '').length;
      }
      if (typeof field === 'string') {
        return field
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean).length;
      }
      return 0;
    };

    const athleteProfile = data.athlete_profile || data.athleteProfile || undefined;
    const coachProfile = data.coach_profile || data.coachProfile || undefined;
    const organizationProfile = data.organization_profile || data.organizationProfile || data.org_profile || data.orgProfile || undefined;
    // _coachDetails: details JSON from the separate coaches table (has sport, bio, city, phone, etc.)
    const coachDetails = data._coachDetails || undefined;
    // _orgContactInfo: contact_info JSON from the separate organizations table (has sports, address, phone, etc.)
    const orgContactInfo = data._orgContactInfo || undefined;
    const orgAddress = orgContactInfo?.address || undefined;
    const roleProfile = athleteProfile || coachProfile || organizationProfile;
    const profile = data.profile || data.user_profile || data.userProfile || roleProfile?.profile || undefined;
    const contactInfo = data.contact_info || data.contactInfo || athleteProfile?.contact_info || athleteProfile?.contactInfo || profile?.contact_info || profile?.contactInfo || undefined;
    const personalDetails =
      data.personal_details ||
      data.personalDetails ||
      athleteProfile?.personal_details ||
      athleteProfile?.personalDetails ||
      profile?.personal_details ||
      profile?.personalDetails ||
      undefined;
    const primaryLocation = this.getStringValue(data.location);
    const city =
      this.getStringValue(data.city) ||
      this.getStringValue(coachDetails?.city) ||
      this.getStringValue(orgAddress?.city) ||
      this.getStringValue(athleteProfile?.city) ||
      this.getStringValue(profile?.city) ||
      this.getStringValue(personalDetails?.city);
    const state =
      this.getStringValue(data.state) ||
      this.getStringValue(coachDetails?.state) ||
      this.getStringValue(orgAddress?.state) ||
      this.getStringValue(athleteProfile?.state) ||
      this.getStringValue(profile?.state) ||
      this.getStringValue(personalDetails?.state);
    const country =
      this.getStringValue(data.country) ||
      this.getStringValue(coachDetails?.country) ||
      this.getStringValue(orgAddress?.country) ||
      this.getStringValue(athleteProfile?.country) ||
      this.getStringValue(profile?.country) ||
      this.getStringValue(personalDetails?.country);
    const fallbackLocation = [city, state, country].filter(Boolean).join(', ');

    return {
      uid: data.uid,
      id: data.id,
      displayName: data.display_name || coachDetails?.fullName || orgContactInfo?.organizationName || orgContactInfo?.contactPerson,
      email: data.email || orgContactInfo?.primaryEmail,
      username: data.username,
      role: data.role || 'athlete',
      isActive: data.is_active,
      isVerified: data.is_verified,
      photoURL: data.photo_url,
      bio: data.bio || coachDetails?.bio || orgContactInfo?.achievements,
      // Use the best available count source to avoid stale 0 values when one source fails.
      postsCount: pickBestCount(data.posts, data.posts_count),
      storiesCount: pickBestCount(data.stories, data.stories_count),
      followersCount:
        (typeof countOverrides?.followersCount === 'number'
          ? countOverrides.followersCount
          : undefined) ??
        Math.max(
          pickBestCount(data.followers_rel, data.followers_count),
          getCollectionCount(data.followers),
          getCount(data.followers)
        ),
      followingCount:
        (typeof countOverrides?.followingCount === 'number'
          ? countOverrides.followingCount
          : undefined) ??
        Math.max(
          pickBestCount(data.following_rel, data.following_count),
          getCollectionCount(data.following),
          getCount(data.following)
        ),
      sports: this.normalizeSports(data),
      sportDetails: Array.isArray(data.sport_details) ? data.sport_details : undefined,
      eventTypes: this.getStringArrayValue(
        data.event_types ||
        data.eventTypes ||
        athleteProfile?.event_types ||
        athleteProfile?.eventTypes ||
        profile?.event_types ||
        profile?.eventTypes
      ),
      position: this.getStringValue(data.position || athleteProfile?.position || profile?.position),
      positionName: this.getStringValue(
        data.position_name ||
        data.positionName ||
        athleteProfile?.position_name ||
        athleteProfile?.positionName ||
        profile?.position_name ||
        profile?.positionName
      ),
      subcategory: this.getStringValue(data.subcategory || athleteProfile?.subcategory || profile?.subcategory),
      subcategoryName: this.getStringValue(
        data.subcategory_name ||
        data.subcategoryName ||
        athleteProfile?.subcategory_name ||
        athleteProfile?.subcategoryName ||
        profile?.subcategory_name ||
        profile?.subcategoryName
      ),
      specializations: this.getStringArrayValue(
        data.specializations ||
        coachDetails?.specialization ||
        coachDetails?.specializations ||
        athleteProfile?.specializations ||
        profile?.specializations
      ),
      athleteProfile,
      location: primaryLocation || fallbackLocation || '',
      country: country || '',
      state: state || '',
      city: city || '',
      dateOfBirth: this.getStringValue(
        data.date_of_birth ||
        data.dateOfBirth ||
        data.dob ||
        coachDetails?.date_of_birth ||
        coachDetails?.dateOfBirth ||
        coachDetails?.dob ||
        athleteProfile?.date_of_birth ||
        athleteProfile?.dateOfBirth ||
        athleteProfile?.dob ||
        personalDetails?.date_of_birth ||
        personalDetails?.dateOfBirth ||
        personalDetails?.dob
      ),
      gender: this.getStringValue(data.gender || coachDetails?.gender || athleteProfile?.gender || personalDetails?.gender || profile?.gender),
      height: this.getStringValue(data.height || athleteProfile?.height || personalDetails?.height || profile?.height),
      weight: this.getStringValue(data.weight || athleteProfile?.weight || personalDetails?.weight || profile?.weight),
      website: this.getStringValue(data.website || orgContactInfo?.website || athleteProfile?.website || profile?.website || contactInfo?.website),
      phone: this.getStringValue(
        data.phone ||
        data.phone_number ||
        data.phoneNumber ||
        data.contact_number ||
        data.contactNumber ||
        coachDetails?.phone ||
        orgContactInfo?.primaryPhone ||
        orgContactInfo?.secondaryPhone ||
        contactInfo?.phone ||
        contactInfo?.phone_number ||
        contactInfo?.phoneNumber ||
        contactInfo?.alternate_phone ||
        contactInfo?.alternatePhone ||
        athleteProfile?.phone ||
        athleteProfile?.phoneNumber
      ),
      mobile: this.getStringValue(
        data.mobile ||
        data.mobile_number ||
        data.mobileNumber ||
        data.whatsapp_number ||
        data.whatsappNumber ||
        coachDetails?.phone ||
        coachDetails?.mobile ||
        orgContactInfo?.primaryPhone ||
        orgContactInfo?.secondaryPhone ||
        contactInfo?.mobile ||
        contactInfo?.mobile_number ||
        contactInfo?.mobileNumber ||
        contactInfo?.phone ||
        contactInfo?.phone_number ||
        contactInfo?.phoneNumber ||
        athleteProfile?.mobile ||
        athleteProfile?.mobileNumber ||
        athleteProfile?.contactNumber
      ),
      isOnline: typeof data.is_online === 'boolean' ? data.is_online : undefined,
      lastSeen: data.last_seen ? new Date(data.last_seen) : undefined,
      privacy: data.privacy || athleteProfile?.privacy,
      settings: data.settings || athleteProfile?.settings,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      status: data.status,
      suspension_reason: data.suspension_reason
    } as any;
  }
}

export const userManagementService = new UserManagementService();
export default userManagementService;
